import axios from 'axios'
import { STORAGE_KEYS } from '@/constants'
import { getAuthToken } from '@/utils/auth'
import { extractTenantId } from '@/utils/tenant'
import localStorageService from './LocalStorageService'

class ForcyteService {
  constructor() {
    this.initialize()
  }

  initialize() {
    this.baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'
    this.token = getAuthToken()
    this.tenantId = extractTenantId()
    this.createAxiosInstance()
  }

  createAxiosInstance() {
    this.axiosInstance = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
    })

    this.axiosInstance.interceptors.request.use(config => {
      if (this.token) config.headers['Authorization'] = `Bearer ${this.token}`
      if (this.tenantId) config.headers['x-tenant-id'] = this.tenantId
      config.headers['Content-Type'] = 'application/json'
      return config
    })

    this.axiosInstance.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          forcyteService.clearToken()
          forcyteService.refresh()
        }
        throw error
      }
    )
  }

  refresh() {
    this.initialize()
  }

  getToken() {
    return this.token
  }

  setToken(token, expiresAt) {
    this.token = token
    localStorageService.setItem(STORAGE_KEYS.AUTH_TOKEN, token)
    localStorageService.setItem(STORAGE_KEYS.AUTH_TOKEN_EXPIRY, expiresAt)
  }

  clearToken() {
    this.token = null
    localStorageService.removeItem(STORAGE_KEYS.AUTH_TOKEN)
    localStorageService.removeItem(STORAGE_KEYS.AUTH_TOKEN_EXPIRY)
  }

  isConfigured() {
    return {
      hasToken: !!this.token,
      hasTenantId: !!this.tenantId,
      baseURL: this.baseURL,
      token: this.token ? '***' + this.token.slice(-4) : null,
      tenantId: this.tenantId,
    }
  }

  async get(url, config = {}) {
    const response = await this.axiosInstance.get(url, config)
    return response.data
  }

  async post(url, data, config = {}) {
    const response = await this.axiosInstance.post(url, data, config)
    return response.data
  }

  async put(url, data, config = {}) {
    const response = await this.axiosInstance.put(url, data, config)
    return response.data
  }

  async patch(url, data, config = {}) {
    const response = await this.axiosInstance.patch(url, data, config)
    return response.data
  }

  async delete(url, config = {}) {
    const response = await this.axiosInstance.delete(url, config)
    return response.data
  }
}

const forcyteService = new ForcyteService()
export default forcyteService
